import Axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";

export interface ExcelColumn {
  title: string;
  dataIndex: string;
}

/** One worksheet of a multi-sheet export. */
export interface ExcelSheet {
  name: string;
  columns: ExcelColumn[];
  data: any[];
}

export interface SingleTableBody {
  title: string;
  columns: ExcelColumn[];
  data: any[];
  filename?: string;
}

export interface MultiSheetBody {
  filename: string;
  sheets: ExcelSheet[];
}

/**
 * Stamp the download datetime onto a base export name. Called at click time so
 * the stamp reflects when the button was pressed, not when the page loaded.
 * The extension is appended by the server, which is why the final name is read
 * back from the response.
 */
export const stampExportName = (exportName: string): string =>
  `${exportName}_${dayjs().format("YYYYMMDDHHmm")}`;

/**
 * The server owns excel generation — it takes columns + rows and writes the
 * workbook, either as one sheet (`SingleTableBody`) or one sheet per entry
 * (`MultiSheetBody`). Rejects on failure so the caller can surface it; note
 * that because the response is a Blob, an error body cannot be read here.
 */
export const downloadExcel = (
  body: SingleTableBody | MultiSheetBody
): Promise<void> =>
  Axios.post(`/excel/download`, body, { responseType: "blob" }).then((res) => {
    if (!res || !res.data) {
      // A session-expiry rejection resolves with `undefined` rather than
      // throwing, so guard before reading the response.
      throw new Error("Export failed: no response");
    }

    saveAs(res.data, readFilename(res.headers, body.filename));
  });

/**
 * Prefer the name the server reports, since it is the only thing that knows
 * which extension was used (.xlsx for one file, .zip once a table is split).
 * Tolerates a quoted or unquoted header and falls back to the requested name,
 * so a header change degrades the filename instead of the download.
 */
const readFilename = (headers: any, requested?: string): string => {
  const fallback = `${requested || "export"}.xlsx`;
  const header = headers && headers["content-disposition"];

  if (typeof header !== "string") return fallback;

  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!match) return fallback;

  try {
    return decodeURIComponent(match[1].trim()) || fallback;
  } catch (err) {
    // A literal % in the name is not valid percent-encoding.
    return match[1].trim() || fallback;
  }
};
